import type { CostBenefitOneTimeCosts } from "@repo/shared/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./one-time-costs-table.i18n";

type OneTimeCostsTableProps = {
  costs: CostBenefitOneTimeCosts;
  currency: string;
};

/**
 * Withdrawal / deposit / currency / total one-time costs of the transition, in
 * the flow currency. QL-538: only the cost lines this transition actually
 * incurs are listed, and nothing renders when it incurs none.
 */
export function OneTimeCostsTable({ costs, currency }: OneTimeCostsTableProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();

  // The server rounds every line to 2 decimals, so a cost too small to show
  // is already 0 here.
  if (costs.total === 0) return null;

  // Two decimals like the cost table, and so the lines add up to the total
  // (the server keeps 2 decimals per line).
  const money = (v: number) =>
    `${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  const rows: Array<{ label: string; value: number; total?: boolean }> = [
    { label: t.withdrawalCosts, value: costs.withdrawal },
    { label: t.depositCosts, value: costs.deposit },
    { label: t.currencyCosts, value: costs.currency },
  ].filter((row) => row.value !== 0);
  rows.push({ label: t.totalOneTimeCosts, value: costs.total, total: true });

  return (
    <Table variant="data" data-testid="cost-benefit-one-time-costs">
      <TableHeader>
        <TableRow>
          <TableHead>{t.oneTimeCosts}</TableHead>
          <TableHead className="text-right" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.label}>
            <TableCell className={row.total ? "font-semibold" : "font-medium"}>
              {row.label}
            </TableCell>
            <TableCell className={`text-right tabular-nums ${row.total ? "font-semibold" : ""}`}>
              {money(row.value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
